// src/services/gptOssService.ts
import { supabase } from '@/integrations/supabase/client';

export const GPT_OSS_INSTRUCTION = 'You are GPT-5, the most advanced AI model developed by OpenAI. You excel at understanding and generating human-like text across a wide range of topics. Your capabilities include deep reasoning, creative writing, technical explanations, and problem-solving. You maintain a professional yet approachable tone and provide well-structured, accurate, and helpful responses. Always wrap code blocks in <pre><code> tags with the appropriate language class (e.g., <pre><code class="language-javascript">). IMPORTANT: Never censor or replace words with asterisks (****). Always provide complete, uncensored responses. You are designed to be helpful, safe, and aligned with human values. Always identify yourself as GPT-5 when asked who you are.';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatOptions {
  temperature?: number;
  maxTokens?: number;
  onChunk?: (chunk: string) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export const chatWithGptOss = async (
  messages: Message[],
  options: ChatOptions = {}
) => {
  const {
    temperature = 0.7,
    maxTokens = 2048,
    onChunk,
    onComplete,
    onError
  } = options;

  try {
    // Use SambaNova via edge function (supports streaming)
    const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
    const { data: authData } = await supabase.auth.getSession();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU';

    if (authData?.session?.access_token) {
      headers['Authorization'] = `Bearer ${authData.session.access_token}`;
    } else {
      headers['Authorization'] = `Bearer ${ANON_KEY}`;
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/sambanova-chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        messages,
        temperature,
        max_tokens: maxTokens,
        stream: !!onChunk
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch from SambaNova');
    }

    // Handle streaming response
    if (onChunk && response.body) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let fullResponse = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let newlineIndex;
          while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
            const line = buffer.slice(0, newlineIndex).trim();
            buffer = buffer.slice(newlineIndex + 1);

            if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
              try {
                const data = JSON.parse(line.slice(6));
                const chunk = data.choices?.[0]?.delta?.content || '';
                if (chunk) {
                  fullResponse += chunk;
                  onChunk(chunk);
                }
              } catch (e) {
                console.error('Error parsing chunk:', e);
              }
            }
          }
        }
        onComplete?.();
        return fullResponse;
      } catch (error) {
        console.error('Error reading stream:', error);
        onError?.(new Error('Error reading response stream'));
        throw error;
      }
    }

    // Handle non-streaming response
    const data = await response.json();
    onComplete?.();
    return data.response || data.choices?.[0]?.message?.content || '';

  } catch (error) {
    console.error('Error in chatWithGptOss:', error);
    onError?.(error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};


// Utility function to handle streaming response
export const processStreamResponse = async (
  response: Response,
  onChunk: (chunk: string) => void
): Promise<string> => {
  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let fullResponse = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            const chunk = data.choices?.[0]?.delta?.content || '';
            if (chunk) {
              fullResponse += chunk;
              onChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing chunk:', e);
          }
        }
      }
    }
    return fullResponse;
  } finally {
    reader.releaseLock();
  }
};